import React, {useEffect, useState} from "react";
import NextLink from "next/link";
import {useParentComponentContext} from "../../context/ParentComponentContext";
import {usePageContext} from "../../context/PageContext";
import CCOLinkText from "./CCOLinkText";
import CCOOpenInNewTabIcon from "./CCOOpenInNewTabIcon";
import {
    generateDownloadTargetFromUrl,
    getUrl,
    resolveLinkWithPlaceholder
} from "../../utility/navigationUtil";
import {useLinkTracking} from "tracking/hooks/useLinkTracking";

interface Props {
    externalMenuLink?: boolean,
    children?: JSX.Element,
    contentId?: string,
    customStyle?: any,
    icon?: JSX.Element,
    name?: string,
    noLinkIcon?: boolean,
    noWrap?: boolean,
    onClick?: any,
    openInTab?: boolean,
    target: any,
    type?: string,
    displayContents?: boolean
    dataCypress?: string
    className?: string
    style?: React.CSSProperties
    tabIndex?: number
    ariaCurrent?: string
    ariaLabel?: string
    isExternalTarget?: boolean
}

const CCOLink = React.forwardRef<HTMLAnchorElement, Props>(function CCOLink(props, ref) {
    const parentComponentContext = useParentComponentContext();
    const pageContext = usePageContext();
    const showLinkIcon = !props.noLinkIcon;
    const display = props.displayContents ? "contents" : props.noWrap ? "inline-block" : "flex"
    const downloadTarget = generateDownloadTargetFromUrl(props.target);
    const linkTarget = isTargetInNewTab(props.target, props.openInTab ? props.openInTab :
        downloadTarget != null) ? "_blank" : "_self";
    const id = props.target && props.target.id ? props.target.id : "noContent";
    const [href, setHref] = useState<string>(() => {
        const internalLinkTarget = (props.target.type === 'SOSInternalLink' || props.target.type ===
                                    'SOSTeaser') ? resolveLinkWithPlaceholder(props.target,
                                                                              typeof window !==
                                                                              'undefined' ?
                                                                                  [localStorage,
                                                                                      sessionStorage] :
                                                                                  null,
                                                                              parentComponentContext.cmId,
                                                                              pageContext.page?.id) :
            null;
        if (downloadTarget != null) {
            return downloadTarget;
        } else if (internalLinkTarget !== null) {
            return internalLinkTarget;
        } else {
            return getUrl(props.target);
        }
    });

    useEffect(() => {
        const internalLinkTarget = (props.target.type === 'SOSInternalLink' || props.target.type ===
                                    'SOSTeaser') ? resolveLinkWithPlaceholder(props.target,
                                                                              typeof window !==
                                                                              'undefined' ?
                                                                                  [localStorage,
                                                                                      sessionStorage] :
                                                                                  null,
                                                                              parentComponentContext.cmId,
                                                                              pageContext.page?.id) :
            null;
        if (downloadTarget != null) {
            setHref(downloadTarget);
        } else if (internalLinkTarget !== null) {
            setHref(internalLinkTarget);
        } else {
            setHref(getUrl(props.target));
        }
    }, [props.target, downloadTarget, parentComponentContext.cmId, pageContext.page?.id]);

    const handleClick = useLinkTracking({
                                            href,
                                            target: linkTarget as any,
                                            onClick: props.onClick,
                                            isExternalOverride: props.isExternalTarget,
                                        });

    if (props.children) {
        return <NextLink ref={ref} tabIndex={props.tabIndex} className={props.className}
                         data-cypress={props.dataCypress || 'ccoLink'}
                         data-info={'ccoLink-' + props.target.id}
                         data-cypress-additional={href}
                         style={{
                             display: props.externalMenuLink ? 'block' : undefined,
                             ...props.style
                         }}
                         target={linkTarget} id={"link-" + id}
                         shallow={false}
                         href={href || '#'} prefetch={false}
                         onClick={handleClick}
                         aria-current={props.ariaCurrent as any}
                         aria-label={props.ariaLabel}>
            {(props.openInTab && showLinkIcon) && <CCOOpenInNewTabIcon/>}
            {props.children}
        </NextLink>
    } else {
        return <NextLink ref={ref} className={props.className}
                         data-cypress={props.dataCypress || 'ccoLink'}
                         data-info={'ccoLink-' + props.target.id}
                         data-cypress-additional={href}
                         style={{display: display, ...props.style}} target={linkTarget}
                         id={"link-" + id} shallow={false}
                         href={href || '#'} prefetch={false}
                         onClick={handleClick}
                         aria-current={props.ariaCurrent as any}
                         aria-label={props.ariaLabel}>
            <CCOLinkText type={props.type} icon={props.icon}
                         displayContents={props.displayContents}>
                <React.Fragment>
                    {(props.openInTab && showLinkIcon && props.name) && <CCOOpenInNewTabIcon/>}
                    {props.name ? props.name : props.target.teaserTitle}
                </React.Fragment>
            </CCOLinkText>
        </NextLink>
    }
});

function isTargetInNewTab(target: any, openInNewTab: boolean) {
    if (!target) {
        return openInNewTab;
    }
    if (typeof target === 'string') {
        return openInNewTab;
    } else {
        if (target.type && (target.type === "CMExternalLink" || target.type === "SOSWimmelbild")) {
            return true;
        }
        if (target.type && target.type === "SOSTeaser") {
            if (target.teaserTarget?.type === "SOSWimmelbild" || target.teaserTarget?.type ===
                "CMExternalLink") {
                return true;
            } else {
                return openInNewTab;
            }
        } else {
            return openInNewTab
        }
    }
}

export default CCOLink